"use client";

import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CategoryTreeNode } from "@/lib/api-types";

type MegaMenuProps = {
  categories: CategoryTreeNode[];
};

const FEATURED_SLUG = "new";

export function MegaMenu({ categories }: MegaMenuProps) {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Плоский список корней в порядке донора. FEATURED ("Новые поступления") —
  // отдельной акцентной плиткой наверху.
  const { featured, roots } = useMemo(() => {
    const featured = categories.find((c) => c.slug === FEATURED_SLUG) ?? null;
    const roots = categories
      .filter((c) => c.slug !== FEATURED_SLUG)
      .sort((a, b) => {
        const aOrder = a.sort_order || Number.MAX_SAFE_INTEGER;
        const bOrder = b.sort_order || Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder;
      });
    return { featured, roots };
  }, [categories]);

  const allFlat = useMemo(
    () => (featured ? [featured, ...roots] : roots),
    [featured, roots],
  );
  const active = allFlat.find((c) => c.id === activeId) ?? allFlat[0];

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        className="inline-flex h-11 items-center gap-2 rounded-[8px] px-3 text-base font-medium hover:bg-bg-muted"
      >
        Каталог
        <svg
          aria-hidden
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && categories.length > 0 && (
        <div
          role="menu"
          className="absolute left-0 top-full z-30 mt-2 w-screen max-w-[960px] rounded-[12px] border border-border bg-white p-4 shadow-xl"
        >
          <div className="grid grid-cols-[300px_1fr] gap-4">
            <div className="max-h-[70vh] overflow-y-auto border-r border-border pr-3">
              {featured && (
                <Link
                  href={`/catalog/${featured.slug}`}
                  onMouseEnter={() => setActiveId(featured.id)}
                  onClick={() => setOpen(false)}
                  className={`mb-3 block rounded-[8px] border border-brand-500 bg-brand-50 px-3 py-2 text-sm font-semibold text-brand-600 hover:bg-brand-100 ${
                    active?.id === featured.id ? "ring-2 ring-[var(--color-brand-500)]" : ""
                  }`}
                >
                  {featured.name}
                </Link>
              )}

              <ul>
                {roots.map((root) => (
                  <li key={root.id}>
                    <Link
                      href={`/catalog/${root.slug}`}
                      onMouseEnter={() => setActiveId(root.id)}
                      onClick={() => setOpen(false)}
                      className={`block rounded-[8px] px-3 py-1.5 text-sm leading-snug hover:bg-bg-muted ${
                        active?.id === root.id ? "bg-bg-muted font-medium" : ""
                      }`}
                    >
                      {root.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="max-h-[70vh] overflow-y-auto">
              {active && (
                <>
                  <Link
                    href={`/catalog/${active.slug}`}
                    onClick={() => setOpen(false)}
                    className="mb-3 inline-block text-base font-semibold text-brand-500 hover:underline"
                  >
                    Все товары в категории {active.name} →
                  </Link>
                  {active.children.length > 0 ? (
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {active.children.map((child) => (
                        <li key={child.id}>
                          <Link
                            href={`/catalog/${active.slug}/${child.slug}`}
                            onClick={() => setOpen(false)}
                            className="block rounded-[8px] px-3 py-1.5 text-sm hover:bg-bg-muted"
                          >
                            {child.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-text-muted">
                      В этой категории нет подкатегорий.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
